import React, { useState } from 'react';
import { X } from 'lucide-react';
import { fmtLong } from './calUtils';

const Field = ({ label, children, hint }) => (
  <label className="block text-xs text-slate-700"><span className="block font-semibold mb-1">{label}</span>{children}{hint && <span className="block text-[10px] text-slate-500 mt-0.5">{hint}</span>}</label>
);

// Booking.com side panel: edit status / rooms to sell / price / min stay for one room over a date range, then preview & push.
export const CellEditor = ({ sel, onClose, onSubmit }) => {
  const d = sel.d || {};
  const [from, setFrom] = useState(sel.iso);
  const [to, setTo] = useState(sel.to || sel.iso);
  const [status, setStatus] = useState(d.numAvail === 0 ? 'closed' : 'open');
  const [avail, setAvail] = useState(d.numAvail ?? '');
  const [price, setPrice] = useState(d.price1 ?? '');
  const [minStay, setMinStay] = useState(d.minStay ?? '');
  const [err, setErr] = useState('');
  const changedStatus = status !== (d.numAvail === 0 ? 'closed' : 'open');
  const save = () => {
    setErr('');
    if (to < from) { setErr('End date is before start date.'); return; }
    const body = { room_id: sel.room.room_id, from_date: from, to_date: to, prices: {}, pct: null, min_stay: null, num_avail: null, note: 'calendar' };
    if (changedStatus) body.num_avail = status === 'closed' ? 0 : Number(avail || sel.room.units);
    else if (avail !== '' && Number(avail) !== d.numAvail) body.num_avail = Number(avail);
    if (price !== '' && Number(price) !== d.price1) body.prices.price1 = Number(price);
    if (minStay !== '' && Number(minStay) !== (d.minStay ?? null)) body.min_stay = Number(minStay);
    if (!Object.keys(body.prices).length && body.min_stay == null && body.num_avail == null) { setErr('Nothing changed yet.'); return; }
    onSubmit(body);
  };
  return (
    <aside className="fixed inset-y-0 right-0 w-full sm:w-[380px] bg-white shadow-2xl border-l z-40 p-5 overflow-y-auto" data-testid="rc-editor">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{sel.room.room}</h3>
          <p className="text-xs text-slate-500">{fmtLong(from)}{to !== from ? ` – ${fmtLong(to)}` : ''} · Standard Rate</p>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Close" data-testid="rc-editor-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-2 py-1.5 w-full" data-testid="rc-editor-from" /></Field>
          <Field label="To"><input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="border rounded px-2 py-1.5 w-full" data-testid="rc-editor-to" /></Field>
        </div>
        <div>
          <span className="block text-xs font-semibold text-slate-700 mb-1">Room status</span>
          <div className="flex gap-4 text-xs">
            <label className="inline-flex items-center gap-1.5"><input type="radio" name="rc-status" checked={status === 'open'} onChange={() => setStatus('open')} data-testid="rc-editor-open" /> Open</label>
            <label className="inline-flex items-center gap-1.5"><input type="radio" name="rc-status" checked={status === 'closed'} onChange={() => setStatus('closed')} data-testid="rc-editor-closed" /> Closed</label>
          </div>
        </div>
        {status === 'open' && <Field label="Rooms to sell" hint={`You have ${sel.room.units} unit(s) of this type.`}><input type="number" min="0" max="50" value={avail} onChange={(e) => setAvail(e.target.value)} className="border rounded px-2 py-1.5 w-full" data-testid="rc-editor-avail" /></Field>}
        <Field label={`Price for ${sel.room.max_people} guest(s) (€)`} hint="Lower occupancies follow the Beds24 per-person rule automatically."><input type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)} className="border rounded px-2 py-1.5 w-full" data-testid="rc-editor-price" /></Field>
        <Field label="Minimum length of stay (nights)"><input type="number" min="1" max="30" value={minStay} onChange={(e) => setMinStay(e.target.value)} className="border rounded px-2 py-1.5 w-full" data-testid="rc-editor-minstay" /></Field>
        {err && <p className="text-xs text-red-700" data-testid="rc-editor-error">{err}</p>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={save} className="bg-[#0071c2] hover:bg-[#005fa3] text-white rounded px-4 py-2 text-xs font-semibold" data-testid="rc-editor-save">Save changes</button>
          <button type="button" onClick={onClose} className="border rounded px-4 py-2 text-xs text-[#0071c2] font-semibold" data-testid="rc-editor-cancel">Cancel</button>
        </div>
        <p className="text-[10px] text-slate-400">Saved to Beds24 → Booking.com, Expedia and the website update within minutes. Every change is logged and can be undone from the history.</p>
      </div>
    </aside>
  );
};
